import Axios from 'axios';
import { action, observable } from 'mobx';
import { API } from '@/config/api';
import { UnderLyingEnum } from '@/@enums/UnderLyingEnum';
import { ScrollPageInfo } from '@/models/data';
import { getExchangeUrl } from '@/utils/location';
import Cookies from 'js-cookie';
import { Notification } from 'element-react';
import i18n from '@/i18n';
import { getBaseUrlBySymbol, getBaseUrlByUnderlyingType } from '@/utils/futures';
import { BALANCETYPE } from '@/config/const';

export interface BalanceDetail {
  availableBalance: string;
  coin: string; // 币种
  balance: string;
  freeze: string;
}

export type SpotAssetCardType = 'BASE' | 'QUOTE' | 'OTHER';

export interface SpotAssetCardItem {
  cardType: SpotAssetCardType;
  coin: string;
  totalAmount: string;
  availableAmount: string;
  frozenAmount: string;
  estimatedAmount: string;
  estimatedCoin: string;
  costPrice?: string | null;
  latestPrice?: string | null;
  profitAmount?: string | null;
  profitRate?: string | null;
  costQuantity?: string | null;
  manualCost?: boolean | null;
  hasTpsl?: boolean | null;
  tpslEntrustCount?: number | null;
  tpslFrozenAmount?: string | null;
  takeProfitCount?: number | null;
  stopLossCount?: number | null;
  tpslEntrustIds?: string[] | null;
}

export interface SpotAssetCardsData {
  symbol: string;
  baseCoin: string;
  quoteCoin: string;
  assetCount: number;
  assets: SpotAssetCardItem[];
}

// export interface CoinBalance extends BalanceDetail {
//   positionMargin: string; // 仓位保证金：∑逐仓仓位保证金 + ∑全仓仓位起始保证金,
//   availableBalanceDisplay: string; // 可用余额 = max(0，max(真实可用，真实可用 + ∑该结算货币下的全仓未实现盈亏))
//   netBalance: string; // 净资产余额 = max(0，真实可用 - abs(∑全仓仓位未实现亏损))
//   floatLoss: string; // 未实现亏损 = ∑该结算货币所有开启全仓浮动亏损之和
//   floatProfit: string; // 未实现盈亏 = ∑所有浮动盈亏之和
//   equity: string; // 账户权益 = 钱包余额 + 未实现盈亏
// }

export interface FundFlowItem {
  id: string;
  createdTime: number;
  type: string;
  symbol: string;
  coin: string;
  amount: string;
}

export interface FundRateItem {
  id: string;
  cast: number;
  createdTime: Date;
  positionSide: PositionSide;
  symbol: string;
  coin: string;
}

export interface CoinConfigItem {
  blockUrl: string;
  chain: string;
  chainId: string;
  cmcCurrencyDetailsLink: string;
  cnName: string;
  coinChainName: string;
  enName: string;
  fullName: string;
  group: string;
  id: string;
  imgUrl: string;
  inConfigTimes: string;
  isConvertOpen: string;
  isPayIn: string;
  isPayOut: string;
  isTransfer: string;
  minPayIn: string;
  name: string;
  needMemo: string;
  outConfigTimes: string;
  outDecimal: string;
  sort: string;
  tradeUrl: string;
}

export interface BonusItem {
  [key: string]: any;
}

export default class Asset {
  @observable balance: BalanceDetail;

  @observable balanceList: any;

  @observable fundFlowList: FundFlowItem[];

  @observable fundFlowListPageInfo: ScrollPageInfo;

  @observable fundRateList: FundRateItem[];

  @observable fundRateListPageInfo: ScrollPageInfo;

  @observable isFetchingFundFlowList: boolean;

  @observable isFetchingFundRateList: boolean;

  @observable isFetchingTransfer: boolean;

  @observable userFundMap: { [key: number]: any };

  @observable leverFundList: any[];

  @observable fundFlowListForPosition: FundFlowItem[];

  @observable fundFlowListPageInfoForPosition: ScrollPageInfo;

  // @observable leverMarketList: any[];

  @observable supportCoinList: any[];

  @observable spotBalanceList: any[];

  @observable tigerBalanceList: any[];

  @observable otcBalanceList: any[];

  @observable financialBalanceList: any[];

  @observable isFetchingBonusList: boolean;

  @observable bonusList: BonusItem[];

  @observable bonusListPageInfo: ScrollPageInfo;

  @observable spotFundFlowTypeArr: any[];

  @observable spotAssetCards: SpotAssetCardsData;

  @observable isFetchingSpotAssetCards: boolean;

  @observable isUpdatingSpotAssetCost: boolean;

  constructor() {
    this.balance = {
      availableBalance: '0',
      coin: '',
      balance: '0',
      freeze: '0',
    };

    this.balanceList = [];

    this.fundFlowList = [];
    this.fundFlowListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.fundRateList = [];
    this.fundRateListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };

    this.isFetchingFundFlowList = false;
    this.isFetchingFundRateList = false;
    this.isFetchingTransfer = false;

    this.fundFlowListForPosition = [];
    this.fundFlowListPageInfoForPosition = {
      hasPrev: false,
      hasNext: false,
    };

    this.userFundMap = {};
    this.leverFundList = [];
    // this.leverMarketList = [];
    this.supportCoinList = [];
    this.spotBalanceList = [];
    this.tigerBalanceList = [];
    this.otcBalanceList = [];
    this.financialBalanceList = [];
    this.isFetchingBonusList = false;
    this.bonusList = [];
    this.bonusListPageInfo = {
      hasPrev: false,
      hasNext: false,
    };
    this.spotFundFlowTypeArr = [];
    this.spotAssetCards = {
      symbol: '',
      baseCoin: '',
      quoteCoin: '',
      assetCount: 0,
      assets: [],
    };
    this.isFetchingSpotAssetCards = false;
    this.isUpdatingSpotAssetCost = false;
  }

  @action.bound
  setLoadingState(key: string, data: boolean) {
    // @ts-ignore
    this[key] = data;
  }

  @action.bound
  setBalanceDetail(data: BalanceDetail) {
    this.balance = data;
  }

  // @action.bound
  // async fetchBalanceDetail(params: { coin: string }) {
  //   const result: BalanceDetail = await Axios.get(API.SPOT_BALANCE_DETAIL, {
  //     params: {
  //       coin: params.coin,
  //     },
  //   });
  //
  //   if (result) {
  //     this.setBalanceDetail(result);
  //   }
  // }

  @action.bound
  updateBalanceDetail(data: BalanceDetail) {
    const coin = data.coin;

    // if (this.balance.coin === coin) {
    //   const newData = { ...this.balance, ...data };
    //   this.setBalanceDetail(newData);
    // }

    const newList: BalanceDetail[] = [...this.spotBalanceList];
    const index = newList.findIndex(item => item.coin === coin);

    if (index > -1) {
      newList[index] = data;
    } else {
      newList.unshift(data);
    }

    this.spotBalanceList = newList;
  }

  @action.bound
  setBalanceList(data: any) {
    this.balanceList = data;
  }

  @action.bound
  setSpotAssetCards(data?: Partial<SpotAssetCardsData>) {
    this.spotAssetCards = {
      symbol: data?.symbol || '',
      baseCoin: data?.baseCoin || '',
      quoteCoin: data?.quoteCoin || '',
      assetCount: data?.assetCount || 0,
      assets: Array.isArray(data?.assets) ? data?.assets || [] : [],
    };
  }

  @action.bound
  async fetchSpotAssetCards(params: { symbol?: string }) {
    const { symbol } = params;

    if (!symbol) {
      this.setSpotAssetCards();
      return;
    }

    this.setLoadingState('isFetchingSpotAssetCards', true);
    this.setSpotAssetCards({ symbol });

    const result: SpotAssetCardsData = await Axios.get(API.SPOT_ASSET_CARDS, {
      params: { symbol },
    });

    if (result) {
      this.setSpotAssetCards(result);
    }

    this.setLoadingState('isFetchingSpotAssetCards', false);
  }

  @action.bound
  async updateSpotAssetCost(params: { symbol: string; costPrice: string }) {
    this.setLoadingState('isUpdatingSpotAssetCost', true);

    const result = await Axios.post(API.SPOT_ASSET_COST_UPDATE, params, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setLoadingState('isUpdatingSpotAssetCost', false);

    return result;
  }

  @action.bound
  async fetchBalanceList() {
    const result: any = await Axios.get(API.SPOT_BALANCE_LIST, { params: { type: BALANCETYPE } });

    if (result) {
      this.setBalanceList(result);

      if (result.spotBalance && result.spotBalance.length > 0) {
        this.spotBalanceList = result.spotBalance;
      }
    }
  }

  @action.bound
  getBalanceByCoin(coin: string): BalanceDetail | undefined {
    const list = Array.isArray(this.spotBalanceList) ? this.spotBalanceList : [];
    const target = coin?.toUpperCase();
    return list.find((item: BalanceDetail) => item.coin?.toUpperCase() === target);
  }

  // 资金流水

  @action.bound
  setFundFlowList(data: FundFlowItem[]) {
    this.fundFlowList = data;
  }

  @action.bound
  setFundFlowListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.fundFlowListPageInfo };
    this.fundFlowListPageInfo = { ...origin, ...data };
  }

  // 资金费率

  @action.bound
  setFundRateList(data: FundRateItem[]) {
    this.fundRateList = data;
  }

  @action.bound
  setFundRateListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.fundRateListPageInfo };
    this.fundRateListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchFundRateList({
    symbol,
    direction,
    limit = 20,
    id,
    startTime,
    endTime,
    underlyingType,
  }: {
    symbol?: string;
    direction: ScrollPaginationDirection;
    limit?: number;
    id?: string;
    startTime?: number;
    endTime?: number;
    underlyingType?: UnderLyingEnum;
  }) {
    this.setLoadingState('isFetchingFundRateList', true);

    const config: any = {
      params: {
        symbol,
        direction,
        limit,
        id,
        startTime,
        endTime,
      },
    };

    if (symbol) {
      config.baseURL = getBaseUrlBySymbol(symbol);
    } else if (underlyingType) {
      config.baseURL = getBaseUrlByUnderlyingType(underlyingType);
    }

    const result: any = await Axios.get(API.SPOT_BALANCE_FUNDING_RATE, config);

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setFundRateList(items || []);
      this.setFundRateListPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingFundRateList', false);
  }

  @action.bound
  setUserFundMap = (account: number, data: any) => {
    this.userFundMap = { ...this.userFundMap, [account]: data };
  };

  @action.bound
  async fetchUserFund(account: string) {
    const params = { type: account };
    const result: any = await Axios.get(getExchangeUrl().balance, {
      params,
    });

    if (result) {
      this.spotBalanceList = result.spotBalance;
      this.tigerBalanceList = result.tigerBalance;
      this.otcBalanceList = result.otcBalance;
      this.financialBalanceList = result.financialBalance;
    }
  }

  @action.bound
  setLeverFundList(data: any) {
    this.leverFundList = data;
  }

  @action.bound
  async fetchUserLeverFund({ account }: { account: number }) {
    const result: any = await Axios.post(
      getExchangeUrl().getUserLeverFund,
      {
        account,
      },
      {
        headers: {
          device: 'web',
        },
      },
    );

    if (result) {
      this.setLeverFundList(result.items || []);
    }
  }

  // @action.bound
  // setLeverMarketList(data: any) {
  //   this.leverMarketList = data;
  // }

  @action.bound
  setSupporCoinList(data: any) {
    this.supportCoinList = data;
  }

  // @action.bound
  // async fetchLeverMarketList() {
  //   const result = await Axios.get(getExchangeUrl().getLeverMarketConfig, {
  //     headers: {
  //       device: 'web',
  //     },
  //   });

  //   if (result) {
  //     this.setLeverMarketList(result);
  //   }
  // }

  @action.bound
  async fetchSupportCoinList() {
    const params = { fromWalletType: 'SPOT', toWalletType: 'CONTRACT' };
    const result = await Axios.get(getExchangeUrl().supportCoin, { params });

    if (result) {
      const coinlist = [];
      result.forEach(element => {
        coinlist.push(element.coin);
      });
      this.setSupporCoinList(coinlist);
    }
  }

  @action.bound
  async fetchTransfer(params: { fromWalletType: string; toWalletType: string; coin: string; amount: number }) {
    this.setLoadingState('isFetchingTransfer', true);

    const instance = Axios.create({
      timeout: 10000,
    });

    await instance
      .post(getExchangeUrl().transferBalance, JSON.stringify(params), {
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
        },
      })
      .then(res => {
        if (res.data.code === -1) {
          Notification.error({
            message: res.data.msg,
            offset: 60,
            duration: 2000,
          });
          return;
        }
        Notification.success({
          message: i18n.t('success.transfer'),
          offset: 60,
          duration: 2000,
        });

        // eslint-disable-next-line consistent-return
        return Promise.resolve();
      })
      .catch(() => {
        Notification.error({
          message: i18n.t('failure.transfer'),
          offset: 60,
          duration: 2000,
        });
      })
      .finally(() => {
        this.setLoadingState('isFetchingTransfer', false);
      });
  }

  // 持仓板块 - 资金流水

  @action.bound
  setFundFlowListForPosition(data: FundFlowItem[]) {
    this.fundFlowListForPosition = data;
  }

  @action.bound
  setFundFlowListPageInfoForPosition(data: ScrollPageInfo) {
    const origin = { ...this.fundFlowListPageInfoForPosition };
    this.fundFlowListPageInfoForPosition = { ...origin, ...data };
  }

  // 体验金
  @action.bound
  setBonusList(data: BonusItem[]) {
    this.bonusList = data;
  }

  @action.bound
  setBonusListPageInfo(data: ScrollPageInfo) {
    const origin = { ...this.bonusListPageInfo };
    this.bonusListPageInfo = { ...origin, ...data };
  }

  @action.bound
  async fetchBonusList({
    direction,
    limit = 20,
    id,
    startTime,
    endTime,
  }: {
    direction: ScrollPaginationDirection;
    limit?: number;
    id?: string;
    startTime?: number;
    endTime?: number;
  }) {
    this.setLoadingState('isFetchingBonusList', true);

    const result: any = await Axios.get(API.SPOT_BALANCE_BILLS, {
      params: {
        direction,
        limit,
        id,
        startTime,
        endTime,
      },
    });

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setBonusList(items || []);
      this.setBonusListPageInfo({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingBonusList', false);
  }

  @action.bound
  setSpotFundFlowTypeArr(data: any[]) {
    this.spotFundFlowTypeArr = data;
  }

  @action.bound
  async fetchSpotFundFlowTypeArr() {
    const result: any = await Axios.get(API.SPOT_FUND_FLOW_CONF);
    if (result) {
      this.setSpotFundFlowTypeArr(result);
    }
  }

  @action.bound
  async fetchFundFlowListForPosition({
    symbol,
    direction,
    limit = 10,
    type,
    id,
    startTime,
    endTime,
  }: {
    symbol?: string;
    direction: ScrollPaginationDirection;
    limit?: number;
    type?: string;
    id?: string;
    startTime?: number;
    endTime?: number;
  }) {
    this.setLoadingState('isFetchingFundFlowList', true);
    const result: any = await Axios.get(API.SPOT_FUND_FLOW, {
      params: {
        symbol,
        direction,
        limit,
        type,
        id,
        startTime,
        endTime,
      },
    });

    if (result) {
      const { hasPrev, hasNext, items } = result;

      this.setFundFlowListForPosition(items || []);
      this.setFundFlowListPageInfoForPosition({ hasPrev, hasNext });
    }

    this.setLoadingState('isFetchingFundFlowList', false);
  }
}
